import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { UserPlus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/StatusBadge";
import { TempPasswordPanel } from "@/components/auth/TempPasswordPanel";

const sel = "h-9 rounded-md border border-slate-300 bg-white text-sm px-2";

const InviteDialog = ({ roles, onClose, onDone }) => {
  const [f, setF] = useState({ email: "", name: "", role: "secretary", phone: "" });
  const [temp, setTemp] = useState(null);
  const go = async () => { try { const r = await api.post("/users", f); toast.success("User invited"); setTemp({ email: r.data.email, password: r.data.temp_password }); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="invite-dialog">
        <DialogHeader><DialogTitle className="font-display">Invite a user</DialogTitle><DialogDescription>They sign in with this email and a one-time temporary password.</DialogDescription></DialogHeader>
        {temp ? <TempPasswordPanel email={temp.email} password={temp.password} /> : <div className="space-y-3">
          <Input placeholder="Email address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email" />
          <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" />
          <select className={`${sel} w-full`} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="invite-role">
            {Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Input placeholder="Phone for SMS/WhatsApp (optional, +countrycode…)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="invite-phone" />
        </div>}
        <DialogFooter>{temp ? <Button onClick={onClose} data-testid="invite-done">Done</Button> : <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={go} disabled={!f.email} data-testid="invite-submit">Invite</Button></>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Users() {
  const { meta, user: me } = useAuth();
  const [list, setList] = useState([]);
  const [params] = useSearchParams();
  const [inv, setInv] = useState(params.get("invite") === "1");
  const [temp, setTemp] = useState(null);
  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { if (meta.permissions.manage_users) load(); }, [meta]);
  if (!meta.permissions.manage_users) return <Navigate to="/dashboard" replace />;
  const isSA = me.role === "super_admin";
  const patch = async (u, body) => { try { await api.patch(`/users/${u.user_id}`, body); toast.success("User updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const reset = async (u) => {
    if (!window.confirm(`Reset ${u.name}'s password? They will be signed out everywhere.`)) return;
    try { const r = await api.post(`/auth/users/${u.user_id}/reset-password`); setTemp({ email: r.data.email, password: r.data.temp_password }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Governance" title="Users & roles" testid="users-title">
        <Button onClick={() => setInv(true)} data-testid="invite-user-button"><UserPlus className="w-4 h-4 mr-1.5" />Invite user</Button>
      </PageHeader>
      <p className="text-sm text-slate-600 mb-4 max-w-2xl">Only invited emails can sign in. Inviting someone gives a one-time temporary password to pass on privately. Staff and financial records are visible only to Super Admin, Secretary, Manager and Auditor. Creators can never approve their own records.</p>
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50/80">
            <th className="px-4 py-2.5">User</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Active</th><th className="px-4 py-2.5 hidden md:table-cell">Last sign-in</th>{isSA && <th className="px-4 py-2.5">Password</th>}</tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.user_id} className="border-b border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3"><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}{u.phone && ` · ${u.phone}`}</div></td>
                <td className="px-4 py-3"><select className={sel} value={u.role} disabled={u.user_id === me.user_id} onChange={(e) => patch(u, { role: e.target.value })} data-testid={`user-role-select-${u.email}`}>
                  {Object.entries(meta.roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
                <td className="px-4 py-3"><Switch checked={u.active !== false} disabled={u.user_id === me.user_id} onCheckedChange={(v) => patch(u, { active: v })} data-testid={`user-active-switch-${u.email}`} /></td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{u.last_login ? fmtDate(u.last_login) : "Never"}</td>
                {isSA && <td className="px-4 py-3">{u.user_id !== me.user_id && <Button size="sm" variant="outline" onClick={() => reset(u)} data-testid={`user-reset-password-${u.email}`}><KeyRound className="w-3.5 h-3.5 mr-1" />Reset password</Button>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {inv && <InviteDialog roles={meta.roles} onClose={() => setInv(false)} onDone={load} />}
      <Dialog open={!!temp} onOpenChange={(o) => !o && setTemp(null)}>
        <DialogContent className="bg-white" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Password reset</DialogTitle><DialogDescription>The user has been signed out everywhere.</DialogDescription></DialogHeader>
          {temp && <TempPasswordPanel email={temp.email} password={temp.password} />}
          <DialogFooter><Button onClick={() => setTemp(null)} data-testid="reset-password-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
